import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import classnames from 'classnames'
import Grid from '@material-ui/core/Grid'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import Drawer from '@material-ui/core/Drawer'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import Chip from '@material-ui/core/Chip'
import Hidden from '@material-ui/core/Hidden'
import OpenInNew from '@material-ui/icons/ZoomOutMap'
import CloseIcon from '@material-ui/icons/Close'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner as SpinnerIcon } from '@fortawesome/free-solid-svg-icons/faSpinner'
import { faPlay as SimulateIcon } from '@fortawesome/free-solid-svg-icons/faPlay'
import { faShoePrints as TrackIcon } from '@fortawesome/free-solid-svg-icons/faShoePrints'
import { ToastContainer, toast } from 'react-toastify'

// contexts
import { useClient } from '../../contexts/ClientContext'

// styles
import 'react-toastify/dist/ReactToastify.css'
import useStyles from './styles'

// components
import PageTitle from '../../components/PageTitle/PageTitle'
import Widget from '../../components/Widget/Widget'
import { Button, Tooltip } from '../../components/Wrappers'
import LogDetails from '../logDetails'
import Notification from '../../components/Notification/Notification'

// utilities
import { useFormInput } from '../../customHooks'
import { getStatusTextSafe } from '../../utils'

export default function AlarmTracker () {
  const classes = useStyles()
  const theme = useTheme()
  const { filter, setFilter, filteredLogs, fetchFilteredLogs, simulateEvent } = useClient()

  const history = useHistory()
  const [filterError, setFilterError] = useState('')
  const [pageProcessing, setPageProcessing] = useState(false)
  const [simulationError, setSimulationError] = useState('')
  const [isTracking, setTracking] = useState(false)
  const [pollState, setPollState] = useState('ready')
  const [pollTimer, setPollTimer] = useState()
  const [showDrawer, setShowDrawer] = useState(false)
  const [lastSelectedId, setLastSelectedId] = useState()

  const canShowDrawer = useMediaQuery(`(min-width:${theme.breakpoints.values.md}px)`)

  const alarmId = useFormInput(filter.alarmId)
  const alarmEvent = useFormInput('none')

  const sendNotification = (componentProps, options) =>
    toast(<Notification {...componentProps} className={classes.notificationComponent} />, options)

  useEffect(() => {
    let aborted = false
    const runEffect = async () => {
      if (pollState === 'pending' && isTracking) {
        setPollState('processing')
        await fetchAlarmLogs(aborted)
        if (aborted) { return }
        setPollState('ready')
      }
    }
    runEffect()
    return () => { aborted = true }
  }, [pollState])

  async function fetchAlarmLogs (abort) {
    try {
      await fetchFilteredLogs(abort = false)
    } catch (error) {
      console.log(error)
      stopTracking()
      if (error.statusCode === 404) setFilterError('Alarm not found.')
      else if (error.statusCode === 400) setFilterError('Invalid alarm identifier.')
      else setFilterError('Something went wrong. Please try again.')
    }
  }

  async function startTracking () {
    if (pollTimer) clearInterval(pollTimer)
    if (filterError) setFilterError('')
    if (!alarmId || !alarmId.value) { return }
    setFilter(filter => Object.assign({}, filter, { alarmId: alarmId.value }))
    setTracking(true)
    setPollState('pending')
    const timer = setInterval(async () => {
      setPollState('pending')
    }, 5000)
    setPollTimer(timer)
  }

  function stopTracking () {
    setTracking(false)
    if (pollTimer) clearInterval(pollTimer)
  }

  const simulateAlarmEvent = async () => {
    if (simulationError) setSimulationError('')
    if (!alarmId.value) { return }
    if (!alarmEvent.value || alarmEvent.value === 'none') { return }
    setPageProcessing(true)
    try {
      await simulateEvent(alarmId.value, alarmEvent.value)
      sendNotification({
        type: 'report',
        message: 'Event simulated successfully!',
        variant: 'contained',
        color: 'success'
      }, {
        type: 'success',
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
        progressClassName: classes.progress,
        className: classes.notification
      })
    } catch (error) {
      console.log(error)
      if (error.statusCode === 404) setFilterError('Alarm not found.')
      else if ((error.statusCode === 400 || error.statusCode === 500) && error.body && error.body.details) {
        setSimulationError(error.body.details)
      } else setSimulationError('Something went wrong. Please try again.')
    }
    setPageProcessing(false)
  }

  const openFullLogView = () => history.push(`/app/logs/${lastSelectedId}`)

  const handleRowClick = (id) => () => {
    setLastSelectedId(id)
    if (canShowDrawer) setShowDrawer(true)
    else history.push(`/app/logs/${id}`)
  }

  return (
    <>
    <ToastContainer
      className={classes.toastsContainer}
      closeButton={<CloseButton className={classes.notificationCloseButton} />}
      closeOnClick={false}
      progressClassName={classes.notificationProgress}
    />
    <Drawer
      PaperProps={{ className: classes.drawer }}
      anchor='right'
      open={showDrawer}
      onClose={() => setShowDrawer(false)}
    >
      <Grid className={classes.drawerToolbar} container justify='flex-end'>
        <Grid item>
          <Tooltip
            title='Page view'
            arrow
            enterDelay={500}
            enterNextDelay={500}
          >
            <IconButton onClick={openFullLogView}>
              <OpenInNew />
            </IconButton>
          </Tooltip>
          <Tooltip
            title='Close'
            arrow
            enterDelay={500}
            enterNextDelay={500}
          >
            <IconButton onClick={() => setShowDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <LogDetails requestId={lastSelectedId} />
    </Drawer>
    <Grid container spacing={4}>
      <Hidden lgDown><Grid item xl={1}></Grid></Hidden>
      <Grid item xs={12} xl={10}>
        <PageTitle title="Alarm Tracker" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Widget disableWidgetMenu >
              <Grid container spacing={4}>
                <Grid container spacing={2} item xs={12}>
                  <Grid item xs={10}>
                    <TextField
                      error={!!filterError}
                      placeholder='Enter Alarm Identifier'
                      variant='outlined'
                      fullWidth
                      helperText={filterError}
                      onFocus={() => setFilterError('')}
                      {...alarmId}
                      disabled={isTracking || pageProcessing}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      className={classes.fatButton}
                      variant="contained"
                      size="large"
                      color={ isTracking ? 'error' : 'primary'}
                      fullWidth
                      onClick={isTracking ? stopTracking : startTracking}
                      disabled={!alarmId.value}
                    >
                      {!isTracking && <FontAwesomeIcon icon={TrackIcon} size='lg' rotation={270} />}
                      {isTracking && <FontAwesomeIcon icon={SpinnerIcon} size='lg' pulse />}
                      <Hidden smDown>
                        <Typography className={classes.pl} variant='body1'>
                          {isTracking ? 'Stop' : 'Track'}
                        </Typography>
                      </Hidden>
                    </Button>
                  </Grid>
                </Grid>
                <Grid container spacing={2} item xs={12}>
                  <Grid item xs={10}>
                    <FormControl variant="outlined" fullWidth>
                      <Select
                        {...alarmEvent}
                      >
                        <MenuItem value="none">
                          <Typography color="textSecondary">Select event to simulate</Typography>
                        </MenuItem>
                        <MenuItem value='alarm.psap_contacted'>
                          <Typography color="textPrimary">alarm.psap_contacted</Typography>
                        </MenuItem>
                        <MenuItem value='alarm.status.canceled'>
                          <Typography color="textPrimary">alarm.status.canceled</Typography>
                        </MenuItem>
                        <MenuItem value='alarm.closed'>
                          <Typography color="textPrimary">alarm.closed</Typography>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      className={classes.fatButton}
                      variant="contained"
                      size="large"
                      color="secondary"
                      fullWidth
                      onClick={() => simulateAlarmEvent()}
                      disabled={
                        !alarmId.value ||
                        (!alarmEvent.value || alarmEvent.value === 'none') ||
                        filterError ||
                        pageProcessing
                      }
                    >
                      <FontAwesomeIcon icon={SimulateIcon} size='lg' />
                      <Hidden smDown>
                        <Typography className={classes.pl} variant='body1'>Simulate</Typography>
                      </Hidden>
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body1' color='error' align='center'>
                      {simulationError}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Widget>
          </Grid>
          <Grid item xs={12}>
            {(!!isTracking || (!!filteredLogs && !!filteredLogs.length)) && <Widget
              disableWidgetMenu
              title='Logs'
            >
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell width={300} align='left'>Status</TableCell>
                      <TableCell align='left'>Description</TableCell>
                      <TableCell align='right'>Request Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!filteredLogs && filteredLogs.map(({ id, request, response }) => {
                      const status = `${response.status} ${getStatusTextSafe(response.status)}`
                      const requestTime = moment(request.time).format('M/D/YYYY, h:mm:ss a')
                      const isErrorStatus = response.status >= 400
                      return (
                        <TableRow
                          hover
                          key={id}
                          style={{ cursor: 'pointer' }}
                          onClick={handleRowClick(id)}
                        >
                          <TableCell width={300} align='left'>
                            <Chip
                              className={classnames(classes.statusChip, {
                                [classes.chipError]: isErrorStatus,
                                [classes.chipSuccess]: !isErrorStatus
                              })}
                              size='small'
                              label={status}
                            />
                          </TableCell>
                          <TableCell align='left'>
                            <Typography
                              display='inline'
                              variant='body2'
                              color='textSecondary'
                            >{request.verb} </Typography>
                            <Typography display='inline' variant='body2'>{request.route}</Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography variant='body2'>{requestTime}</Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Widget>}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
    </>
  )
}

function CloseButton ({ closeToast, className }) {
  return <CloseIcon className={className} onClick={closeToast} />
}
